import { useMemo, useState, useEffect, useRef } from 'react'
import { Globe, TrendingUp, Sparkles, Bookmark, Check, BarChart3, Filter, MapPin, Calendar } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
// Removed content ideas accordion

const COUNTRIES = ['US', 'IN', 'GB'] as const

import type { TrendItem } from '@/services/global-trends'
import { fetchTrends, fetchTrendCategories, saveTrendCard } from '@/services/global-trends'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'

// Data loaded from API

function TopicCard({ topic, onSaved }: { topic: TrendItem; onSaved?: () => void }) {
  const { toast } = useToast()
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const formatQuantity = (n?: number) => {
    if (!n || n <= 0) return null
    const abs = Math.abs(n)
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(n % 1_000_000_000 === 0 ? 0 : 1)}B+`
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M+`
    if (abs >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K+`
    return `${n}+`
  }
  return (
    <Card className="transition-all hover:shadow-md hover:shadow-black/5 dark:hover:shadow-white/5 border border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <CardTitle className="text-lg font-semibold text-foreground line-clamp-1">{topic.websearch_query}</CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              <span className="line-clamp-1">{topic.summary}</span>
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">
              {topic.geo}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {topic.category}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="inline-flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
            <span className="font-medium text-foreground">{topic.trendScore}</span>
            <span>trend score</span>
          </div>
          {typeof topic.quantity === 'number' && topic.quantity > 0 ? (
            <div className="inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
              <span className="font-medium text-foreground">{formatQuantity(topic.quantity)}</span>
              <span>mentions</span>
            </div>
          ) : null}
          {topic.sources?.length ? (
            <div className="inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
              <span>{topic.sources.length} sources</span>
            </div>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between mb-2">
          <div />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant={isSaved ? 'default' : 'outline'}
                  onClick={async () => {
                    if (isSaving || isSaved) return
                    setIsSaving(true)
                    try {
                      const resp = await saveTrendCard({
                        id: topic.id,
                        geo: topic.geo,
                        category: topic.category,
                        websearch_query: topic.websearch_query,
                        summary: topic.summary,
                        quantity: topic.quantity,
                        trendScore: topic.trendScore,
                        sources: topic.sources,
                      })
                      if (resp?.alreadySaved) {
                        toast({ title: 'Already saved', description: 'This trend is already in your collection.' })
                        setIsSaved(true)
                      } else {
                        setIsSaved(true)
                        toast({ title: 'Saved', description: 'Trend saved to your collection.' })
                      }
                      onSaved?.()
                    } catch (e) {
                      toast({ title: 'Save failed', description: 'Please try again.', variant: 'destructive' })
                    } finally {
                      setIsSaving(false)
                    }
                  }}
                  disabled={isSaving || isSaved}
                >
                  {isSaved ? (
                    <>
                      <Check className="mr-2 h-3 w-3" /> Saved
                    </>
                  ) : (
                    <>
                      <Bookmark className="mr-2 h-3 w-3" /> {isSaving ? 'Saving...' : 'Save'}
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isSaved ? 'Trend saved' : 'Save to your collection'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {topic.summary && (
          <div className="text-sm text-muted-foreground">
            <details className="transition-all">
              <summary className="cursor-pointer select-none py-2 px-1 rounded hover:bg-muted/40">
                Read more
              </summary>
              <div className="mt-3 space-y-3">
                <MarkdownRenderer content={topic.summary} />
                {topic.sources?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {topic.sources.map((s, i) => (
                      <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline underline-offset-2"
                      >
                        {s.title}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function GlobalTrendsPage() {
  const [country, setCountry] = useState<typeof COUNTRIES[number]>('US')
  const [category, setCategory] = useState<string>('All')
  const [categoryOptions, setCategoryOptions] = useState<string[]>(['All'])
  const [items, setItems] = useState<TrendItem[]>([])
  const [lastKey, setLastKey] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [trendMin, setTrendMin] = useState<number>(0)
  const [mentionsMin, setMentionsMin] = useState<number>(0)
  const trendsAbortRef = useRef<AbortController | null>(null)
  const trendsReqIdRef = useRef(0)

  const load = async (reset = true) => {
    // Cancel previous
    if (trendsAbortRef.current) trendsAbortRef.current.abort()
    const controller = new AbortController()
    trendsAbortRef.current = controller
    const reqId = ++trendsReqIdRef.current

    const currentGeo = country
    const currentCategory = category
    setLoading(true)
    try {
      if (reset) {
        setItems([])
        setLastKey(null)
      }
      const resp = await fetchTrends(
        {
          geo: currentGeo,
          category: currentCategory === 'All' ? undefined : currentCategory,
          minScore: 0,
          limit: 20,
          lastKey: reset ? undefined : lastKey,
        },
        { signal: controller.signal }
      )
      // Guard against stale responses
      if (reqId !== trendsReqIdRef.current) return
      if (currentGeo !== country || currentCategory !== category) return
      setItems((prev) => (reset ? resp.items : [...prev, ...resp.items]))
      setLastKey(resp.last_evaluated_key || null)
    } catch (err: any) {
      if (err?.name === 'AbortError') return
      // swallow network errors here; surface via toast if desired
    } finally {
      if (reqId === trendsReqIdRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, category])

  const categoriesAbortRef = useRef<AbortController | null>(null)
  const categoriesReqIdRef = useRef(0)
  useEffect(() => {
    if (categoriesAbortRef.current) categoriesAbortRef.current.abort()
    const controller = new AbortController()
    categoriesAbortRef.current = controller
    const reqId = ++categoriesReqIdRef.current
    const selectedGeo = country
    fetchTrendCategories({ geo: selectedGeo }, { signal: controller.signal })
      .then((res) => {
        if (reqId !== categoriesReqIdRef.current) return
        if (selectedGeo !== country) return
        const opts = Array.isArray(res.categories) ? res.categories : []
        setCategoryOptions(['All', ...opts])
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return
        setCategoryOptions(['All'])
      })
    return () => controller.abort()
  }, [country])

  const displayItems = useMemo(() => {
    return items.filter((it) => (it.trendScore ?? 0) >= trendMin && (it.quantity ?? 0) >= mentionsMin)
  }, [items, trendMin, mentionsMin])

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-400/10 to-transparent dark:from-blue-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                Global Trends
              </CardTitle>
              <p className="text-muted-foreground">Curated from across the internet on what topics people are talking about</p>
            </div>
            <Badge variant="secondary" className="text-xs">
              {displayItems.length} Active
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Trends</CardTitle>
            <BarChart3 className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{displayItems.length}</div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <p className="text-xs text-blue-600">Currently trending</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-green-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Countries</CardTitle>
            <MapPin className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{COUNTRIES.length}</div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <p className="text-xs text-green-600">Regions covered</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
            <Filter className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-500">{Math.max(0, categoryOptions.length - 1)}</div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <p className="text-xs text-purple-600">Available filters</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-amber-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
            <Calendar className="h-5 w-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">
              {(() => {
                const t = items.reduce<number>((max, it) => {
                  const ts = typeof it.current_timestamp === 'number' ? it.current_timestamp : 0
                  return ts > max ? ts : max
                }, 0)
                return t
                  ? new Date(t * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : 'N/A'
              })()}
            </div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <p className="text-xs text-amber-600">Data refresh</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Section */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-50/50 dark:bg-slate-950/20 flex-shrink-0">
              <Filter className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Filters & Controls</CardTitle>
              <CardDescription>
                Customize your trend discovery experience
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Region</Label>
        <Tabs value={country} onValueChange={(v) => setCountry(v as typeof COUNTRIES[number])}>
          <TabsList>
            {COUNTRIES.map((c) => (
              <TabsTrigger key={c} value={c} className="px-4">
                {c}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v)}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Category</SelectLabel>
                {categoryOptions.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <Label htmlFor="trend-score-slider" className="text-sm font-medium">Trend Score Threshold</Label>
              <div className="flex items-center gap-3">
                <Slider
                  id="trend-score-slider"
                  min={0}
                  max={100}
                  step={1}
                  value={[trendMin]}
                  onValueChange={(v) => setTrendMin(v[0])}
                  className="flex-1"
                />
                <Badge variant="outline" className="min-w-[3rem] text-center">
                  {trendMin}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">Minimum trend score to display</p>
            </div>

            <div className="space-y-3">
              <Label htmlFor="mentions-slider" className="text-sm font-medium">Mentions Threshold</Label>
              <div className="flex items-center gap-3">
                <Slider
                  id="mentions-slider"
                  min={0}
                  max={500000}
                  step={100}
                  value={[mentionsMin]}
                  onValueChange={(v) => setMentionsMin(v[0])}
                  className="flex-1"
                />
                <Badge variant="outline" className="min-w-[4rem] text-center">
                  {mentionsMin.toLocaleString()}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">Minimum mentions to display</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        {displayItems.map((t) => (
          <TopicCard key={t.id} topic={t} />
        ))}
        {displayItems.length === 0 && loading && (
          <>
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="group hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                      <Skeleton className="h-5 w-2/3" />
                        <Skeleton className="h-5 w-16" />
                      </div>
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-16" />
                    </div>
                    </div>
                  </div>
                  <Skeleton className="h-3 w-full mt-2" />
                  <Skeleton className="h-3 w-3/4" />
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex justify-end">
                    <Skeleton className="h-8 w-20" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>
      
      {lastKey && (
        <div className="flex justify-center">
          <Button variant="outline" disabled={loading} onClick={() => load(false)} className="w-full max-w-xs">
            {loading ? (
              <>
                <Sparkles className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More Trends'
            )}
          </Button>
        </div>
      )}
      
      {displayItems.length === 0 && !loading && (
      <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted/50 flex-shrink-0">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg font-semibold">No Trends Found</CardTitle>
                <CardDescription>
                  Try adjusting your filters or selecting a different region to discover active conversations.
          </CardDescription>
              </div>
            </div>
        </CardHeader>
        </Card>
      )}
    </div>
  )
}
